import React from "react";

/**
 * Minimal markdown renderer for AI chat output.
 * Handles the patterns Claude actually returns from our tools:
 *  - ### Heading       → small section title
 *  - **bold**          → <strong>
 *  - newlines          → spaced lines
 *  - "1. ", "2. ", ... → numbered list rows with proper RTL
 *  - "- ", "• ", "· "  → bulleted list rows
 *  - blank lines       → visible section gap
 *
 * Designed for Hebrew RTL: bullets/numbers float to the right edge.
 */
export default function MD({ text, className = "" }) {
  if (!text) return null;
  const rawLines = String(text).split(/\r?\n/);

  // Group consecutive list items so we can render them as a single visual block
  // with tighter internal spacing but bigger gaps between sections.
  const blocks = groupBlocks(rawLines);

  return (
    <div className={`leading-relaxed space-y-2.5 ${className}`}>
      {blocks.map((b, i) => renderBlock(b, i))}
    </div>
  );
}

function groupBlocks(lines) {
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    if (line.trim() === "") {
      // collapse multiple blank lines into a single gap block
      while (i < lines.length && lines[i].trim() === "") i++;
      blocks.push({ type: "gap" });
      continue;
    }

    // Heading (### or ##)
    const headingMatch = line.match(/^\s*#{2,3}\s+(.+)$/);
    if (headingMatch) {
      blocks.push({ type: "heading", text: headingMatch[1] });
      i++;
      continue;
    }

    // Numbered list (collect consecutive items)
    if (/^\s*\d+[\.\)]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*(\d+)[\.\)]\s+/.test(lines[i])) {
        const m = lines[i].match(/^\s*(\d+)[\.\)]\s+(.*)$/);
        items.push({ num: m[1], text: m[2] });
        i++;
      }
      blocks.push({ type: "ol", items });
      continue;
    }

    // Bullet list (collect consecutive items)
    if (/^\s*[-•·]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-•·]\s+/.test(lines[i])) {
        const m = lines[i].match(/^\s*[-•·]\s+(.*)$/);
        items.push(m[1]);
        i++;
      }
      blocks.push({ type: "ul", items });
      continue;
    }

    // Paragraph — collect consecutive non-blank, non-list, non-heading lines
    const para = [];
    while (
      i < lines.length &&
      lines[i].trim() !== "" &&
      !/^\s*#{2,3}\s+/.test(lines[i]) &&
      !/^\s*\d+[\.\)]\s+/.test(lines[i]) &&
      !/^\s*[-•·]\s+/.test(lines[i])
    ) {
      para.push(lines[i]);
      i++;
    }
    blocks.push({ type: "p", text: para.join(" ") });
  }
  return blocks;
}

function renderBlock(b, key) {
  switch (b.type) {
    case "gap":
      return <div key={key} className="h-1.5" />;
    case "heading":
      return (
        <div
          key={key}
          className="text-[11px] font-semibold text-primary uppercase tracking-wider pt-1.5 pb-0.5"
        >
          {renderInline(b.text)}
        </div>
      );
    case "ol":
      return (
        <div key={key} className="space-y-1.5">
          {b.items.map((it, idx) => (
            <div key={idx} className="flex items-start gap-2">
              <span className="text-primary font-mono text-[11px] mt-0.5 shrink-0 min-w-[1.1rem] text-right">
                {it.num}.
              </span>
              <span className="flex-1 min-w-0">{renderInline(it.text)}</span>
            </div>
          ))}
        </div>
      );
    case "ul":
      return (
        <div key={key} className="space-y-1.5">
          {b.items.map((it, idx) => (
            <div key={idx} className="flex items-start gap-2">
              <span className="text-primary mt-1 shrink-0 leading-none">•</span>
              <span className="flex-1 min-w-0">{renderInline(it)}</span>
            </div>
          ))}
        </div>
      );
    case "p":
    default:
      return (
        <div key={key}>{renderInline(b.text)}</div>
      );
  }
}

function renderInline(s) {
  // Tokenise **bold** while keeping order
  const parts = [];
  const regex = /\*\*([^*]+)\*\*/g;
  let lastIdx = 0;
  let m;
  let key = 0;
  while ((m = regex.exec(s)) !== null) {
    if (m.index > lastIdx) parts.push({ t: "txt", v: s.slice(lastIdx, m.index), k: key++ });
    parts.push({ t: "b", v: m[1], k: key++ });
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < s.length) parts.push({ t: "txt", v: s.slice(lastIdx), k: key++ });

  return parts.map((p) =>
    p.t === "b" ? (
      <strong key={p.k} className="font-semibold text-foreground">{p.v}</strong>
    ) : (
      <React.Fragment key={p.k}>{p.v}</React.Fragment>
    )
  );
}
